import { React, useEffect, useState } from "react";
import AppWrapper from "../AppWrapper";
import { useNavigate, useSearchParams } from "react-router-dom";
import supabase from "../../supabase-client";
import {
  Page,
  Button,
  LegacyCard,
  IndexTable,
  Text,
  Pagination,
  TextField,
  BlockStack,
  Spinner,
  Badge,
  Select,
  Link,
} from "@shopify/polaris";
import { numberWithCommas, formatPlate } from "../../utility/funcs";
import { SearchIcon } from "@shopify/polaris-icons";
import { UserAuth } from "../../context/AuthContext";
import * as XLSX from "xlsx/xlsx.mjs";
import {
  applyHeaderFormats,
  moneyHeaders,
  toExcelSerial,
  INTEGER_FMT,
  DATE_ONLY_FMT,
} from "../../utility/xlsxExport";

function Cars() {
  const navigate = useNavigate();
  const { hasPermission } = UserAuth();
  // URL is the source of truth for `q`, `sort`, `page` so navigating back
  // from a car detail restores the same view.
  const [searchParams, setSearchParams] = useSearchParams();
  const [cars, setCars] = useState([]);
  const [search, setSearch] = useState(searchParams.get("q") || "");
  const [page, setPage] = useState(
    Math.max(1, parseInt(searchParams.get("page") || "1", 10) || 1)
  );
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  // Format: "<field>_<asc|desc>" — matches the Select option values below.
  const [sortKey, setSortKey] = useState(
    searchParams.get("sort") || "created_at_desc"
  );

  // Serialize current state back into the URL. Only writes non-default values.
  const syncUrl = (next = {}) => {
    const params = {};
    const q = next.search ?? search;
    const s = next.sortKey ?? sortKey;
    const p = next.page ?? page;
    if (q) params.q = q;
    if (s && s !== "created_at_desc") params.sort = s;
    if (p && p !== 1) params.page = String(p);
    setSearchParams(params, { replace: true });
  };

  const sortOptions = [
    { label: "Modelo (A → Z)", value: "model_asc" },
    { label: "Modelo (Z → A)", value: "model_desc" },
    { label: "Año (más reciente)", value: "year_desc" },
    { label: "Año (más antiguo)", value: "year_asc" },
    { label: "Odómetro (mayor)", value: "odometer_desc" },
    { label: "Odómetro (menor)", value: "odometer_asc" },
    { label: "Creado (más reciente)", value: "created_at_desc" },
    { label: "Creado (más antiguo)", value: "created_at_asc" },
  ];

  const pageSize = 20;

  const fetchCars = async () => {
    setLoading(true);

    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;
    const lastUnderscore = sortKey.lastIndexOf("_");
    const sort_by = sortKey.substring(0, lastUnderscore);
    const sort_dir = sortKey.substring(lastUnderscore + 1);

    const { data, error } = await supabase.rpc("search_cars", {
      query_text: search?.trim() || null,
      from_index: from,
      to_index: to,
      p_branch_id: null,
      sort_by,
      sort_dir,
    });

    if (error) {
      console.error("Error fetching cars:", error.message);
      setLoading(false);
      return;
    }

    // Data returned already includes model + brand info
    setCars(data);

    // If fewer results than expected, no more pages
    setHasMore(data?.length >= pageSize);
    setLoading(false);
  };

  useEffect(() => {
    fetchCars();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, sortKey]);

  const rowMarkup = cars.map((car, index) => (
    <IndexTable.Row
      id={car.id}
      key={car.id}
      position={index}
    >
      <IndexTable.Cell>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <Link
            url={`/cars/${car.id}`}
            removeUnderline
            monochrome
            dataPrimaryLink
          >
            <Text fontWeight="semibold">{formatPlate(car.plate)}</Text>
          </Link>
          {car.active === false && <Badge tone="critical">Inactivo</Badge>}
        </div>
      </IndexTable.Cell>
      <IndexTable.Cell>{car.model_show_as || car.model_name}</IndexTable.Cell>
      <IndexTable.Cell>{car.vin}</IndexTable.Cell>
      <IndexTable.Cell>{car.color}</IndexTable.Cell>
      <IndexTable.Cell>{car.year}</IndexTable.Cell>
      <IndexTable.Cell>{numberWithCommas(car.odometer)} km</IndexTable.Cell>
    </IndexTable.Row>
  ));

  const primaryAction = () => {
    if (!hasPermission("cars.create")) return;
    return (
      <Button variant="primary" onClick={() => navigate("/cars/edit")}>
        Crear Auto
      </Button>
    );
  };

  const exportAsExcel = async () => {
    setIsExporting(true);
    const { data, error } = await supabase.rpc("get_all_car_data");
    if (error) {
      console.error(error);
      return;
    }
    const cleaned = data.map((row) => ({
      ID: row.id ?? "",
      Marca: row.brand ?? "",
      Modelo: row.model ?? "",
      Apodo: row.show_as ?? "",
      Sucursal: row.branch ?? "",
      Año: row.year ?? "",
      Placa: row.plate ?? "",
      "No. de Serie": row.vin ?? "",
      Estado: row.gps_current_state ?? "",
      Factura: row.invoice_id ?? "",
      Activo: row.active ?? "",
      "Costo de Adquisicion": row.acquisition_price ?? "",
      "Costo de Adaptaciones": row.equipment_cost ?? "",
      "Inicio de Operaciones": toExcelSerial(row.start_date) ?? "",
      Odómetro: row.odometer ?? "",
      "Km Último Cambio de Aceite": row.last_services?.["1"]?.odo ?? "",
    }));

    const worksheet = XLSX.utils.json_to_sheet(cleaned);
    // Costs become real numeric cells with a currency format (so they can be
    // summed), odometers/years plain numbers, and the start date a date cell.
    applyHeaderFormats(worksheet, {
      ...moneyHeaders(["Costo de Adquisicion", "Costo de Adaptaciones"]),
      "Inicio de Operaciones": DATE_ONLY_FMT,
      Odómetro: INTEGER_FMT,
      "Km Último Cambio de Aceite": INTEGER_FMT,
    });
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Autos");

    XLSX.writeFile(workbook, "Lista de Autos - GoMove.xlsx");
    setIsExporting(false);
  };

  return (
    <AppWrapper>
      <Page
        title="Autos"
        primaryAction={primaryAction()}
        secondaryActions={
          <Button onClick={exportAsExcel} loading={isExporting}>
            Exportar Todo
          </Button>
        }
      >
        <BlockStack gap={400}>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              syncUrl({ search, page: 1 });
              if (page === 1) {
                fetchCars();
              } else {
                setPage(1);
              }
            }}
          >
            <div style={{ display: "flex", gap: "0.5rem", alignItems: "flex-end" }}>
              <span style={{ flexGrow: 1 }}>
                <TextField
                  autoComplete="off"
                  placeholder="Buscar por placa, VIN, modelo o marca"
                  value={search}
                  onChange={(value) => setSearch(value)}
                  disabled={loading}
                />
              </span>
              <span
                style={{
                  aspectRatio: 1,
                  display: "flex",
                  alignItems: "center",
                  width: 32,
                  height: 32,
                }}
              >
                <Button icon={SearchIcon} submit disabled={loading} />
              </span>
              <span style={{ minWidth: 200 }}>
                <Select
                  label="Ordenar"
                  labelInline
                  options={sortOptions}
                  value={sortKey}
                  onChange={(val) => {
                    setSortKey(val);
                    setPage(1);
                    syncUrl({ sortKey: val, page: 1 });
                  }}
                  disabled={loading}
                />
              </span>
            </div>
          </form>
          {loading ? (
            <Spinner size="small" />
          ) : (
            <LegacyCard>
              <>
                <IndexTable
                  resourceName={{ singular: "Vehículo", plural: "Vehículos" }}
                  selectable={false}
                  itemCount={cars?.length ?? 0}
                  headings={[
                    { title: "Placa" },
                    { title: "Modelo" },
                    { title: "No de Serie" },
                    { title: "Color" },
                    { title: "Año" },
                    { title: "Odómetro" },
                  ]}
                >
                  {rowMarkup}
                </IndexTable>
                <div
                  style={{
                    padding: "1rem",
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <Pagination
                    hasPrevious={page > 1}
                    onPrevious={() => {
                      const next = page - 1;
                      setPage(next);
                      syncUrl({ page: next });
                    }}
                    hasNext={hasMore}
                    onNext={() => {
                      const next = page + 1;
                      setPage(next);
                      syncUrl({ page: next });
                    }}
                  />
                </div>
              </>
            </LegacyCard>
          )}
        </BlockStack>
      </Page>
    </AppWrapper>
  );
}

export default Cars;
